import React from 'react';
import styled from 'styled-components';
import { v1 as uuidv1 } from 'uuid';

import {
  useMutateRemoveDelegatedKey,
  useQueryWalletDelegatedKeyData,
} from '#/api/queries/delegatedKeysQueries';
import { getWalletProofSignatureTypedData } from '#/api/signatures';
import { useConfirmationModal } from '#/components/basic/modal/ConfirmationModal';
import { signTypedDataWithModal } from '#/lib/ethereum/sign/signHashWithModal';
import { fullLogoutAndDispatch } from '#/reduxState/currentUser/currentUserUtils';
import { useSelector } from '#/reduxState/store';

import type { DelegatedKeyAuthorization } from '#/reduxState/delegatedKey/authorizationReducer';

const Wrapper = styled.div`
  line-height: 1.5;
  color: var(--gray4c);
`;

function ModalWrapper({
  currentDelegatedKey,
  delegatedKey,
}: {
  currentDelegatedKey: DelegatedKeyAuthorization;
  delegatedKey?: string;
}) {
  const { data } = useQueryWalletDelegatedKeyData(delegatedKey);

  return (
    <Wrapper>
      {
        data?.hasOpenOrders ? (
          <>
            {data.hasOpenOrders} order
            {data.hasOpenOrders > 1 && 's'} will be canceled.
            {/* Add line-break */}
            <p />
          </>
        ) : null
        // <>
        //   This Key has no active orders.
        //   <br />
        // </>
      }

      {currentDelegatedKey.walletAddress.toLowerCase() ===
        delegatedKey?.toLowerCase() && (
        <>
          This will lock the current wallet and you will need to unlock your
          wallet again to proceed.
        </>
      )}
    </Wrapper>
  );
}

export function useRevokeModal(currentDelegatedKey: DelegatedKeyAuthorization) {
  const wallet = useSelector(state => state.wallet);
  const delegatedWallet = useSelector(
    state => state.delegatedKey.authorization
  );

  const revokeQuery = useMutateRemoveDelegatedKey();

  return useConfirmationModal<{
    delegatedKey?: string;
  }>({
    confirmText: 'Remove Session Key',
    title: 'Remove Session Key',
    children: data => (
      <ModalWrapper
        delegatedKey={data?.delegatedKey}
        currentDelegatedKey={currentDelegatedKey}
      />
    ),
    confirmButtonProps: {
      color: 'red',
      bordered: true,
      autoFocus: true,
    },
    onConfirm: async ({ delegatedKey } = {}) => {
      if (!delegatedKey) {
        // this is not going to happen but types required it?
        throw new Error('Failed to revoke Session key');
      }
      const nonce = uuidv1();
      const signature = await signTypedDataWithModal(
        wallet,
        undefined,
        await getWalletProofSignatureTypedData({ nonce })
      );
      await revokeQuery.mutateAsync({
        delegatedKey,
        signature,
        nonce,
      });
      // TODO : How to handle if revoke active delegatedKey?  This will log them out.
      if (
        currentDelegatedKey.walletAddress &&
        currentDelegatedKey.walletAddress.toLowerCase() ===
          delegatedKey?.toLowerCase()
      ) {
        await fullLogoutAndDispatch(wallet, delegatedWallet);
      }
    },
  });
}
